import axios from 'axios';
import {forEach, get, set} from 'lodash';
import {_transformContentResponse} from '../../services/content/models';
import {Endpoints} from './endpoints';
import {IFilter} from '../../util/url';
import {url} from './util';
import {
  IAddress,
  IApplicationOptions,
  IChannel,
  ICustomer,
  IPolicyApplication,
  IPostcodeAddress,
  IPostcodeLookup,
  IProduct,
  IVehicle,
  IVehicleLookup,
} from './models';

import {
  application,
  channels,
  customer,
} from './transformers';

import * as moment from 'moment';

const CancelToken = axios.CancelToken;
let cancel = null;

export function listContents(filters?: IFilter[]) {
  return axios
    .get(url(Endpoints.CONTENT_LIST, { filters }))
    .then((response) => {
      return { data: response.data.data.map((res) => _transformContentResponse(res)) };
    });
}

export function createPolicyApplication(policyApplication: IPolicyApplication,
                                        channelId: string,
                                        brokerId: string,
                                        options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  const extraParams = {
    calculate: 'false',
  };

  if (options.calculate) {
    extraParams.calculate = 'true';
  }

  return axios
    .post(
      url(
        Endpoints.POLICY_APPLICATIONS,
        {
          params: { id: policyApplication.id, ...extraParams },
          includes: options.calculate ? ['premiums.scheme', 'customer'] : [],
        },
      ),
      { data: application.toJsonApi(policyApplication, channelId, brokerId) },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function updatePolicyApplication(policyApplication: IPolicyApplication,
                                        options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  if (cancel) {
    cancel();
  }

  const extraParams = {
    calculate: 'false',
  };

  if (options.calculate) {
    extraParams.calculate = 'true';
  }

  const request = options.patch ? axios.patch : axios.put;

  return request(
      url(
        Endpoints.POLICY_APPLICATION,
        {
          params: { id: policyApplication.id, ...extraParams },
          includes: options.calculate ? ['premiums.scheme', 'customer', 'channel_scheme_documents'] : [],
        },
      ),
      { data: application.toJsonApi(policyApplication) },
      { headers: { 'Content-Type': 'application/vnd.api+json' },
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        }),
      },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function saveQuote(policyApplication: IPolicyApplication,
                          channelId: string,
                          options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  const extraParams = {
    calculate: 'false',
  };

  if (options.calculate) {
    extraParams.calculate = 'true';
  }

  return axios
    .post(
      url(
        Endpoints.POLICY_APPLICATIONS,
        {
          params: { id: policyApplication.id, ...extraParams },
          includes: options.calculate ? ['premiums.scheme', 'customer'] : [],
        },
      ),
      { data: application.toJsonApi(policyApplication, channelId) },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function getPolicyApplicationPaymentStatus(policyApplication: IPolicyApplication) {
  return axios
    .post(
      url(Endpoints.POLICY_APPLICATION_STATUS, { params: { id: policyApplication.id } }),
      { meta: { scheme_id: policyApplication.schemeId } },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

// Card payments are taken before the policy is issued. When the card has to be entered with the provider the API
// answers with a 'redirect-to-provider' error holding the transaction id to open in the payment iframe.
export function takePaymentForPolicyApplication(policyApplication: IPolicyApplication, channelKey: string) {
  return axios
    .post(
      url(Endpoints.POLICY_APPLICATION_PAY, { params: { id: policyApplication.id } }),
      {
        meta: {
          payment_type: policyApplication.paymentType || 'card',
          channel_key: channelKey,
          scheme_id: policyApplication.schemeId,
          // a website order: core's card payment chooses the gateway message type (ECOM or telephone) from this
          payment_information: { interaction: 'ecom' },
        },
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function issuePolicyApplication(policyApplication: IPolicyApplication, customerPaymentMethodId?: string) {
  const card = policyApplication.card;
  const reuseAddress = policyApplication.reuseCustomerAddress;

  const billingDetails = {
    name: get(card, 'billing.name', ''),
    email: get(card, 'billing.email', ''),
    phone: get(card, 'billing.phone', ''),
    address: {
      postcode: get(card, 'billing.address.postcode', ''),
      line1: get(card, 'billing.address.line1', ''),
      line2: get(card, 'billing.address.line2', ''),
      town: get(card, 'billing.address.town', ''),
      county: get(card, 'billing.address.county', ''),
    },
  };

  const relationships: any = {
    scheme: {
      data: {
        id: policyApplication.schemeId,
      },
    },
  };

  if (customerPaymentMethodId) {
    relationships.customer_payment_method = {
      data: {
        id: customerPaymentMethodId,
      },
    };
  }

  return axios
    .post(
      url(Endpoints.POLICY_APPLICATION_ISSUE, { params: { id: policyApplication.id } }),
      {
        meta: {
          payment_type: policyApplication.paymentType,
          sms_send: policyApplication.sendSms,
          purchase_declaration: policyApplication.purchaseDeclaration,
          // card_number: card && card.cardNumber.replace(/ /g, ''),
          // cvv: card && card.cvv,
          // expiry_month: card && Number(card.expiryMonth),
          // expiry_year: card && Number(card.expiryYear),
          payment_date: policyApplication.paymentDate,
          billing: reuseAddress ? {} : billingDetails,
        },
        data: {
          relationships,
        },
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function vehicleExcesses(schemeId: string, vehicles: IVehicle[]) {
  const values = [];
  vehicles.forEach((vehicle) => {
    values.push({
      value: vehicle.value,
    });
  });

  return axios
    .put(
      url(Endpoints.VEHICLE_EXCESSES, { params: { scheme: schemeId } }),
      {
        vehicles: {
          horseboxes: values,
        },
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function compulsoryPolicyExcess(schemeId: string, postcode: string, vehicles: IVehicle[]) {
  return axios
    .put(
      url(Endpoints.COMPULSORY_POLICY_EXCESS, { params: { scheme: schemeId } }),
      {
        postcode,
        vehicles,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function checkCampaignCode(code, chanelId: string) {
  return axios
    .get(
      url(Endpoints.CAMPAIGN_CODE, { params: { code, channel: chanelId } }),
    )
    .then((response) => {
      return response.data;
    });
}

export function getChannel(key: string): Promise<IChannel> {
  return axios
    .post(url(Endpoints.CHANNEL, { params: { key }, includes: [ 'products', 'product_documents' ] }))
    .then((response) => {
      return channels.fromJsonApi(response.data.data, response.data.included);
    });
}

// export function getCustomer(key: string): Promise<ICustomer> {
//   return axios
//     .post(url(Endpoints.CUSTOMER_PORTAL, { params: { key }, includes: [ 'customer' ] }))
//     .then((response) => {
//       return customer.fromJsonApi(response.data.data, response.data.included);
//     });
// }

export function lookupPostcode(postcode: string): Promise<IPostcodeLookup[]> {
  return axios
    .get(
      url(Endpoints.POSTCODE_LOOKUP, { params: { postcode } }),
    )
    .then((response) => {
      return response.data.data.map((lookup) => ({
        id: lookup.id,
        line: lookup.attributes.name,
      }));
    });
}

export function getPostcode(id: string): Promise<IPostcodeAddress> {
  return axios
    .get(
      url(Endpoints.POSTCODE_GET, { params: { id } }),
    )
    .then((response) => {
      return response.data.data.attributes;
    });
}

export function getPaymentUrl(transactionId: string) {
  return axios.defaults.baseURL + '/' + url(Endpoints.TRANSACTION_PAY, {
    params: {
      id: transactionId,
    },
  });
}

export function getCustomer() {
  return axios
    .get(
      url(Endpoints.CUSTOMER, {
        includes: ['policies.documents', 'policies.premiums', 'quotes.documents',
          'quotes.premiums', 'quotes.schemes', 'policies.policy_applications.transactions',
          'policies.superseded_policy',
        ] }),
    )
    .then((response) => {
      return customer.fromJsonApi(response.data.data, response.data.included);
    });
}

export function updateCustomer(customerValues: ICustomer) {
  return axios
    .patch(
      url(Endpoints.CUSTOMER, {
        includes: ['policies.documents', 'policies.premiums',
          'quotes.documents', 'quotes.premiums'] }),
      { data: customer.toJsonApi(customerValues) },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return customer.fromJsonApi(response.data.data, response.data.included);
    });
}

export function updateCustomerEmailAndTelephone(values) {
  return axios
    .patch(
      url(Endpoints.UPDATE_CUSTOMER_DETAILS),
      {
        meta: {
          email: values.email,
          phone1: values.phone1,
          password: values.password,
        },
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

export function passwordReminder(email) {
  return axios
    .post(
      url(Endpoints.FORGOTTEN_PASSWORD),
      { email },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

export function accountCreator(email) {
  return axios
    .post(
      url(Endpoints.FORGOTTEN_PASSWORD),
      { email },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

// noinspection TsLint
export function resetPassword(token, { password, passwordConfirmation, email, dob }) {
  return axios
    .post(
      url(Endpoints.PASSWORD_RESET),
      {
        token,
        password,
        password_confirmation: passwordConfirmation,
        dob : moment(dob, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        email,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

export function documentsPoll(documentsPollToken: string) {
  return axios
    .post(
      url(Endpoints.POLICY_DOCUMENTS_POLL),
      { documentsPollToken },
      { headers: { 'Content-Type': 'application/vnd.api+json' }},
    )
    .then((response) => {
      return response.data;
    });
}

export function referralQuote(id, referrals) {
  const referralReasons = referrals.map((referral) => referral.reason);
  const updateData = {};
  set(updateData, 'data.attributes.metadata.referral_information.referred', true);
  set(updateData, 'data.attributes.metadata.referral_information.reasons', referralReasons);
  set(updateData, 'data.attributes.metadata.referral_information.referred_at', moment().format());
  return axios
    .patch(
      url(Endpoints.POLICY_APPLICATION, { params: { id }}),
      updateData,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    ).then(() => {
      return axios
        .post(
          url(Endpoints.REFERRAL_QUOTE, { params: { id }}),
          { meta: { referrals }},
          { headers: { 'Content-Type': 'application/vnd.api+json' } },
        );
    });
}

export function getPremiumsByPaymentSchedules(product: IProduct, policyApplication: IPolicyApplication) {
  const payload = application.toJsonApi(policyApplication);
  payload.meta.group_by = [
    { name: 'annual', fields: { payment_schedule: 'annual' } },
    { name: 'bi_annual', fields: { payment_schedule: 'bi-annual' } },
  ];

  payload.relationships.product = {
    data: {
      id: product.id,
    },
  };

  return axios
    .post(
      url(Endpoints.PREMIUMS_BY_PAYMENT_SCHEDULES),
      { data: payload },
    )
    .then((response) => {
      return {
        annualGrossPremiumForPeriod: response.data.meta.annual.gross,
        biannualGrossPremiumForPeriod: response.data.meta.bi_annual.gross,
      };
    });
}

export function checkExistingCustomer(data) {
  return axios
    .post(
      url(Endpoints.CHECK_EXISTING_CUSTOMER),
      data,
    )
    .then((response) => {
      return response.data;
    });
}

export function vehicleLookup(registration: string): Promise<IVehicleLookup> {
  return axios
    .get(
      url(Endpoints.VEHICLE_LOOKUP, { params: { registration: encodeURIComponent(registration) } }),
    )
    .then((response) => {
      const lookup: IVehicleLookup = {
        make: get(response.data, 'data.attributes.make'),
        model: get(response.data, 'data.attributes.model'),
        year: get(response.data, 'data.attributes.year'),
        value: get(response.data, 'data.attributes.price'),
        weight: get(response.data, 'data.attributes.revenue_weight'),
      };
      return lookup;
    })
    .catch((err) => {
      return Promise.reject(err.response);
    });
}

export function enrichmentCheck(
  address: IAddress,
  companyName: string,
  companyRegistrationNumber: string,
  vehicles: IVehicle[],
  renewal: boolean,
) {
  const formattedVehicles = { agricultural: [] };
  forEach(vehicles, (vehicle) => {
    formattedVehicles.agricultural.push({
      registration_number: vehicle.registrationNumber,
    });
  });
  return axios
    .post(
      url(Endpoints.ENRICHMENT_CHECK), {
        meta: {
          address,
          company_name: companyName,
          company_registration_number: companyRegistrationNumber,
          vehicles: formattedVehicles,
          mta: false,
          renewal,
        },
      },
    )
    .then((response) => {
      return response.data.meta;
    })
    .catch((err) => {
      return Promise.reject(err.response);
    });
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/data/api/api.ts